/**
 * A small, typed `fetch` wrapper over the generated OpenAPI {@link paths} — a
 * wrapper, not a framework. It carries the three auth modes By My Word uses
 * (see the implementation plan, "Auth" + "Referee tokens" + WP-B16a):
 *
 *   - {@link candidateClient} — candidate JWT as `Authorization: Bearer <token>`.
 *   - {@link refereeClient}   — the emailed `selector.verifier` token as a header,
 *     plus `credentials: 'include'` for the scoped cookie session minted on accept.
 *   - {@link staffClient}     — staff JWT as `Authorization: Bearer <token>`, for
 *     the `/api/admin` rail only.
 *
 * ## Why the candidate and staff rails are separated *at the type level*
 *
 * A staff token and a candidate token ride in the SAME transport — both are HS256
 * JWTs in an `Authorization: Bearer` header. What separates them is the audience
 * claim: staff tokens carry the base audience + `:staff` plus a required `purpose`
 * claim, and each scheme validates its own audience exactly, so a candidate token
 * is rejected by the admin rail and a staff token by the candidate rail (proven
 * both directions by the Api's `StaffTokenSeparationTests`).
 *
 * That distinction lives inside the token and is therefore INVISIBLE to a client
 * wrapper: passing the wrong token still produces a well-formed request that only
 * the server can reject. The OpenAPI document does not describe it either — it
 * declares no security schemes at all. So the one place the rails can be kept
 * apart on the client is the path domain, and that is what these factories do:
 * {@link staffClient} reaches only `/api/admin/*`, and the candidate/referee
 * clients reach only everything else. Handing a candidate token to an admin call
 * is a compile error, not a 401 discovered at runtime.
 *
 * Request/response bodies are typed off the schema; a non-2xx response throws an
 * {@link ApiError} carrying parsed RFC 7807 {@link ProblemDetails}.
 *
 * All factories are generic over the paths type with a default of the real
 * generated {@link paths}, so production callers get full typing with zero type
 * arguments, while tests can bind a synthetic schema.
 */
import type { paths as GeneratedPaths } from './schema';

/** RFC 7807 problem details, as emitted by ASP.NET Core's ProblemDetails/ValidationProblem. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  /** Present on validation problems: field name -> messages. */
  errors?: Record<string, string[]>;
  [key: string]: unknown;
}

/** Thrown for any non-2xx response. Carries parsed {@link ProblemDetails} when the body is JSON. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | undefined;
  readonly response: Response;

  constructor(status: number, problem: ProblemDetails | undefined, response: Response) {
    super(problem?.title ?? problem?.detail ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.response = response;
  }
}

export type HttpMethod = 'get' | 'put' | 'post' | 'delete' | 'patch';

/** A value usable in a query string. `undefined` entries are dropped. */
export type QueryValue = string | number | boolean | undefined;

/** Returns the current candidate access token (or null/undefined when signed out). */
export type AccessTokenProvider = () => string | null | undefined | Promise<string | null | undefined>;

export interface ClientOptions {
  /** Injected `fetch` implementation (tests, SSR, retries). Defaults to `globalThis.fetch`. */
  fetchImpl?: typeof fetch;
  /** Headers merged into every request (lowest precedence). */
  headers?: HeadersInit;
}

// --- schema extraction helpers ------------------------------------------------
// These operate structurally so they no-op gracefully while the generated schema
// is empty (paths = Record<string, never>) and tighten automatically once real
// endpoints land.

/**
 * The prefix every staff-authenticated route sits under. The admin rail is mounted
 * as one group (`/api/admin`), which is what makes a prefix a sound proxy for "needs
 * a staff token" — including `/api/admin/auth/*`, which is the rail's own
 * unauthenticated sign-in surface and so is reachable with no token at all.
 */
type AdminPrefix = '/api/admin';

/** Paths on the staff rail — the only ones a staff token authorizes. */
export type AdminPath<TPaths = GeneratedPaths> = Extract<keyof TPaths, `${AdminPrefix}${string}`>;

/** Paths off the staff rail — candidate and referee territory. */
export type NonAdminPath<TPaths = GeneratedPaths> = Exclude<keyof TPaths, AdminPath<TPaths>>;

/** The schema narrowed to the staff rail. */
type AdminPaths<TPaths> = Pick<TPaths, AdminPath<TPaths>>;

/** The schema narrowed to everything off the staff rail. */
type NonAdminPaths<TPaths> = Pick<TPaths, NonAdminPath<TPaths>>;

type OperationOf<TPaths, P extends keyof TPaths, M extends HttpMethod> =
  TPaths[P] extends Record<M, infer O> ? O : never;

/** Paths on which method `M` is defined. */
type PathsWithMethod<TPaths, M extends HttpMethod> = {
  [P in keyof TPaths]: TPaths[P] extends Record<M, unknown> ? P : never;
}[keyof TPaths];

/**
 * The `application/json` request body of an operation (required or optional), or
 * `never` when the operation takes no body at all.
 *
 * The `never` case has to be established BEFORE looking for `content`, and the guard
 * has to test for `undefined` rather than `never`. openapi-typescript spells "this
 * method takes no body" as `requestBody?: never`, and an optional `never` property
 * has type `never | undefined` — i.e. plain `undefined`. Matching that against
 * `{ content: { 'application/json': infer B } }` yields no inference candidate for
 * `B`, so `B` silently falls back to `unknown`; `[unknown] extends [never]` is false,
 * and {@link RequestOptions} then demands a `body` on every GET. That is not
 * hypothetical — it is exactly what happened, and it stayed hidden because a caller
 * only trips it once it passes an options object at all (`query`/`pathParams`).
 *
 * Testing `[RB] extends [undefined]` covers `requestBody: never` too, since `never`
 * is assignable to `undefined` — so both spellings of "no body" collapse correctly.
 */
type JsonBody<O> =
  O extends { requestBody?: infer RB }
    ? [RB] extends [undefined]
      ? never
      : NonNullable<RB> extends { content: { 'application/json': infer B } }
        ? B
        : never
    : never;

/** The `application/json` body of an operation's first 2xx response, else `unknown`. */
type JsonSuccess<O> =
  O extends { responses: infer R }
    ? SuccessBody<R> extends infer B
      ? [B] extends [never] ? unknown : B
      : unknown
    : unknown;

type SuccessBody<R> = {
  [S in keyof R]: `${S & number}` extends `2${string}`
    ? R[S] extends { content: { 'application/json': infer T } }
      ? T
      : never
    : never;
}[keyof R];

/** Per-request options; `body` is present and typed only when the operation takes one. */
export type RequestOptions<TPaths, P extends keyof TPaths, M extends HttpMethod> = {
  /** Path-parameter values substituted into `{name}` placeholders. */
  pathParams?: Record<string, string | number>;
  /** Query-string values; `undefined` entries are omitted. */
  query?: Record<string, QueryValue>;
  /** Per-request headers (highest precedence). */
  headers?: HeadersInit;
  signal?: AbortSignal;
} & ([JsonBody<OperationOf<TPaths, P, M>>] extends [never]
  ? { body?: undefined }
  : { body: JsonBody<OperationOf<TPaths, P, M>> });

/** The typed client surface, keyed off the schema {@link paths}. */
export interface ApiClient<TPaths> {
  get<P extends PathsWithMethod<TPaths, 'get'>>(
    path: P,
    options?: RequestOptions<TPaths, P, 'get'>,
  ): Promise<JsonSuccess<OperationOf<TPaths, P, 'get'>>>;
  post<P extends PathsWithMethod<TPaths, 'post'>>(
    path: P,
    options?: RequestOptions<TPaths, P, 'post'>,
  ): Promise<JsonSuccess<OperationOf<TPaths, P, 'post'>>>;
  put<P extends PathsWithMethod<TPaths, 'put'>>(
    path: P,
    options?: RequestOptions<TPaths, P, 'put'>,
  ): Promise<JsonSuccess<OperationOf<TPaths, P, 'put'>>>;
  patch<P extends PathsWithMethod<TPaths, 'patch'>>(
    path: P,
    options?: RequestOptions<TPaths, P, 'patch'>,
  ): Promise<JsonSuccess<OperationOf<TPaths, P, 'patch'>>>;
  delete<P extends PathsWithMethod<TPaths, 'delete'>>(
    path: P,
    options?: RequestOptions<TPaths, P, 'delete'>,
  ): Promise<JsonSuccess<OperationOf<TPaths, P, 'delete'>>>;
}

// --- runtime ------------------------------------------------------------------

/** Applies auth to the outgoing request (mutates headers, may set credentials). */
interface AuthStrategy {
  apply(headers: Headers): void | Promise<void>;
  readonly credentials?: RequestCredentials;
}

interface RawRequestOptions {
  pathParams?: Record<string, string | number>;
  query?: Record<string, QueryValue>;
  headers?: HeadersInit;
  signal?: AbortSignal;
  body?: unknown;
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}

function applyPathParams(path: string, pathParams?: Record<string, string | number>): string {
  if (!pathParams) return path;
  return path.replace(/\{([^}]+)\}/g, (whole, name: string) => {
    const value = pathParams[name];
    return value === undefined ? whole : encodeURIComponent(String(value));
  });
}

async function parseProblem(response: Response): Promise<ProblemDetails | undefined> {
  try {
    const data = (await response.clone().json()) as unknown;
    return typeof data === 'object' && data !== null ? (data as ProblemDetails) : undefined;
  } catch {
    return undefined;
  }
}

async function parseSuccess(response: Response): Promise<unknown> {
  if (response.status === 204 || response.headers.get('content-length') === '0') return undefined;
  const text = await response.text();
  if (text.length === 0) return undefined;
  return JSON.parse(text) as unknown;
}

function createClient<TPaths>(
  baseUrl: string,
  auth: AuthStrategy,
  options: ClientOptions,
): ApiClient<TPaths> {
  const doFetch = options.fetchImpl ?? globalThis.fetch;
  if (typeof doFetch !== 'function') {
    throw new TypeError('No fetch implementation available; pass options.fetchImpl.');
  }

  async function request(method: HttpMethod, path: string, opts: RawRequestOptions): Promise<unknown> {
    const url = new URL(joinUrl(baseUrl, applyPathParams(path, opts.pathParams)));
    if (opts.query) {
      for (const [key, value] of Object.entries(opts.query)) {
        if (value !== undefined) url.searchParams.set(key, String(value));
      }
    }

    const headers = new Headers(options.headers);
    await auth.apply(headers);
    if (opts.headers) new Headers(opts.headers).forEach((value, key) => headers.set(key, value));
    headers.set('accept', 'application/json');

    let body: string | undefined;
    if (opts.body !== undefined) {
      headers.set('content-type', 'application/json');
      body = JSON.stringify(opts.body);
    }

    const response = await doFetch(url, {
      method: method.toUpperCase(),
      headers,
      body,
      signal: opts.signal,
      ...(auth.credentials ? { credentials: auth.credentials } : {}),
    });

    if (!response.ok) throw new ApiError(response.status, await parseProblem(response), response);
    return parseSuccess(response);
  }

  const method =
    (m: HttpMethod) =>
    (path: string, opts?: RawRequestOptions) =>
      request(m, path, opts ?? {});

  return {
    get: method('get'),
    post: method('post'),
    put: method('put'),
    patch: method('patch'),
    delete: method('delete'),
  } as unknown as ApiClient<TPaths>;
}

/**
 * Candidate client — attaches the candidate JWT as `Authorization: Bearer`.
 * `getAccessToken` is called per request so a refreshed token is always used;
 * when it returns null/undefined the header is omitted (unauthenticated call).
 *
 * Reaches every path EXCEPT the `/api/admin` rail: a candidate token carries the
 * base audience and the admin rail validates the `:staff` audience, so such a call
 * could only ever 401. Use {@link staffClient} for admin routes.
 */
export function candidateClient<TPaths = GeneratedPaths>(
  baseUrl: string,
  getAccessToken: AccessTokenProvider,
  options: ClientOptions = {},
): ApiClient<NonAdminPaths<TPaths>> {
  const auth: AuthStrategy = {
    async apply(headers) {
      const token = await getAccessToken();
      if (token) headers.set('authorization', `Bearer ${token}`);
    },
  };
  return createClient<NonAdminPaths<TPaths>>(baseUrl, auth, options);
}

/** Header carrying the referee `selector.verifier` token. Provisional until WP-B9/B11 fix the contract. */
export const REFEREE_TOKEN_HEADER = 'X-Referee-Token';

export interface RefereeClientOptions extends ClientOptions {
  /** Override the referee token header name (default {@link REFEREE_TOKEN_HEADER}). */
  headerName?: string;
}

/**
 * Referee client — attaches the emailed `selector.verifier` token as a header and
 * sends `credentials: 'include'` so the scoped cookie session (minted on accept)
 * rides along. Referee endpoints are not JWT-authenticated.
 *
 * Reaches every path EXCEPT the `/api/admin` rail (see {@link staffClient}).
 */
export function refereeClient<TPaths = GeneratedPaths>(
  baseUrl: string,
  token: string,
  options: RefereeClientOptions = {},
): ApiClient<NonAdminPaths<TPaths>> {
  const headerName = options.headerName ?? REFEREE_TOKEN_HEADER;
  const auth: AuthStrategy = {
    credentials: 'include',
    apply(headers) {
      headers.set(headerName, token);
    },
  };
  return createClient<NonAdminPaths<TPaths>>(baseUrl, auth, options);
}

/**
 * Staff client — the admin console's rail. Attaches the STAFF JWT as
 * `Authorization: Bearer` and reaches only `/api/admin/*`.
 *
 * A staff token is not a candidate token wearing a different hat: it is minted by
 * the staff OTP rail (`/api/admin/auth/*`), carries the `:staff` audience plus a
 * `purpose` claim, and is rejected outright by every candidate endpoint. Keep the
 * two in separate stores; do not feed a candidate token to this factory.
 *
 * `getAccessToken` is called per request so a rotated token is always used, and may
 * return null/undefined while signed out — which is the normal state for the
 * `/api/admin/auth/*` sign-in calls, the one part of this rail that takes no token.
 *
 * `credentials` is deliberately NOT set: the admin SPA is a scripted client on its
 * own origin holding its token in memory, not a cookie session.
 */
export function staffClient<TPaths = GeneratedPaths>(
  baseUrl: string,
  getAccessToken: AccessTokenProvider,
  options: ClientOptions = {},
): ApiClient<AdminPaths<TPaths>> {
  const auth: AuthStrategy = {
    async apply(headers) {
      const token = await getAccessToken();
      if (token) headers.set('authorization', `Bearer ${token}`);
    },
  };
  return createClient<AdminPaths<TPaths>>(baseUrl, auth, options);
}
